import React, { useState } from 'react';
import { X, Check, Gift } from 'lucide-react';
import { precioConOpciones } from '../../utils/opcionesHelper';

const formatCLP = (n) =>
  '$' + new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(Math.round(n || 0));

// ─── Modal tamaño + agregados ────────────────────────────────────────────────
// soloVer: ficha para mirar (carta del mesón); muestra precios pero no agrega nada
export default function ModalOpciones({ item, onConfirmar, onCerrar, soloVer = false }) {
  const variantes = item.variantes || [];
  const agregados = item.agregados || [];
  const [variante, setVariante] = useState(() => variantes[0] || null);
  const [elegidos, setElegidos] = useState([]);

  const toggleAgregado = (ag) => setElegidos(prev =>
    prev.includes(ag.id) ? prev.filter(x => x !== ag.id) : [...prev, ag.id]);

  const agregadosSel = agregados.filter(a => elegidos.includes(a.id));
  const total = precioConOpciones(item.precioVenta || 0, variante, agregadosSel);
  const listo = variantes.length === 0 || !!variante;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-black text-lg text-gray-900 leading-tight">{item.nombre}</h3>
              {item.descripcion && <p className="text-xs text-gray-500 mt-1">{item.descripcion}</p>}
            </div>
            <button onClick={onCerrar} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 shrink-0">
              <X size={20} />
            </button>
          </div>

          {soloVer && (item.imagen || item.foto) && (
            <img src={item.imagen || item.foto} alt={item.nombre} className="w-full h-48 object-contain rounded-2xl bg-gray-50" />
          )}

          {/* Combo: qué incluye y entre qué se elige */}
          {soloVer && (item.componentes || []).map(c => (
            <div key={c.id} className="text-sm">
              <p className="font-bold text-gray-700">
                {c.tipo === 'elige' ? `A elegir: ${c.nombre}` : `Incluye: ${c.nombre}`}
              </p>
              {c.items?.length > 0 && (
                <p className="text-gray-500 text-xs mt-0.5">{c.items.map(i => i.nombre).join(' · ')}</p>
              )}
            </div>
          ))}

          {/* Tamaños */}
          {variantes.length > 0 && (
            <div>
              <p className="text-sm font-bold text-gray-700 mb-2">{soloVer ? 'Tamaños' : 'Elige el tamaño'}</p>
              <div className="space-y-2">
                {variantes.map(v => {
                  const sel = variante?.id === v.id;
                  return (
                    <button key={v.id} onClick={() => setVariante(v)}
                      className={`w-full p-3.5 rounded-2xl border-2 flex items-center justify-between transition-all
                        ${sel ? 'bg-indigo-50 border-indigo-400' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-2.5">
                        <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0
                          ${sel ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'}`}>
                          {sel && <span className="w-2 h-2 rounded-full bg-white" />}
                        </span>
                        <span className={`font-bold text-sm ${sel ? 'text-indigo-800' : 'text-gray-700'}`}>{v.nombre}</span>
                      </div>
                      <span className={`font-black text-sm ${sel ? 'text-indigo-700' : 'text-gray-500'}`}>{formatCLP(v.precio)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Agregados */}
          {agregados.length > 0 && (
            <div>
              <p className="text-sm font-bold text-gray-700 mb-1">{soloVer ? 'Agregados' : '¿Le agregamos algo?'}</p>
              <p className="text-xs text-gray-400 mb-2">Puedes elegir todos los que quieras</p>
              <div className="space-y-2">
                {agregados.map(a => {
                  const sel = elegidos.includes(a.id);
                  return (
                    <button key={a.id} onClick={() => toggleAgregado(a)}
                      className={`w-full p-3.5 rounded-2xl border-2 flex items-center justify-between transition-all
                        ${sel ? 'bg-emerald-50 border-emerald-400' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-2.5">
                        <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0
                          ${sel ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'}`}>
                          {sel && <Check size={13} className="text-white" strokeWidth={3} />}
                        </span>
                        <span className={`font-bold text-sm ${sel ? 'text-emerald-800' : 'text-gray-700'}`}>{a.nombre}</span>
                      </div>
                      <span className={`font-black text-sm ${sel ? 'text-emerald-700' : 'text-gray-500'}`}>+{formatCLP(a.precio)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {soloVer ? (
            <div className="w-full py-3.5 rounded-2xl bg-gray-900 text-white text-center">
              <p className="font-black">{formatCLP(total)}</p>
              <p className="text-xs text-gray-300">Pídelo en caja</p>
            </div>
          ) : <button
            disabled={!listo}
            onClick={() => onConfirmar({ variante, agregados: agregadosSel, precioUnitario: total })}
            className={`w-full py-3.5 rounded-2xl font-bold text-white transition-colors
              ${listo ? 'bg-[#D32F2F] hover:bg-[#B71C1C] active:scale-95' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
            {listo ? `Agregar al pedido — ${formatCLP(total)}` : 'Elige un tamaño'}
          </button>}
        </div>
      </div>
    </div>
  );
}

// ─── Modal selección opciones combo ──────────────────────────────────────────
export const ModalEligeCombo = ({ combo, onConfirmar, onCerrar }) => {
  const componentesElige = (combo.componentes || []).filter(c => c.tipo === 'elige');
  const [selecciones, setSelecciones] = useState(() => {
    const init = {};
    componentesElige.forEach(c => { init[c.id] = null; });
    return init;
  });
  const completo = componentesElige.every(c => selecciones[c.id] !== null);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg text-gray-900">Personaliza tu combo</h3>
            <button onClick={onCerrar} className="p-2 rounded-full hover:bg-gray-100 text-gray-500">
              <X size={20} />
            </button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
            <Gift size={24} className="text-amber-500 shrink-0" />
            <div>
              <p className="font-bold text-gray-900">{combo.nombre}</p>
              <p className="text-sm text-amber-700 font-semibold">{formatCLP(combo.precio)}</p>
              {combo.descripcion && <p className="text-xs text-gray-500 mt-0.5">{combo.descripcion}</p>}
            </div>
          </div>

          {/* Componentes fijos */}
          {(combo.componentes || []).filter(c => c.tipo === 'fijo').length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Incluye</p>
              <div className="space-y-1">
                {(combo.componentes || []).filter(c => c.tipo === 'fijo').map(c => (
                  <div key={c.id} className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="w-2 h-2 rounded-full bg-green-400 shrink-0" />
                    <span className="font-medium">{c.nombre}</span>
                    {c.items?.length > 0 && (
                      <span className="text-gray-400 text-xs">({c.items.map(i => i.nombre).join(', ')})</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Elecciones */}
          {componentesElige.map(comp => (
            <div key={comp.id}>
              <p className="text-sm font-semibold text-gray-700 mb-2">
                Elige <span className="text-brand-600">{comp.nombre}</span>:
              </p>
              <div className="grid grid-cols-2 gap-2">
                {(comp.items || []).map(item => {
                  const sel = selecciones[comp.id]?.id === item.id;
                  return (
                    <button key={item.id}
                      onClick={() => setSelecciones(p => ({ ...p, [comp.id]: item }))}
                      className={`p-3 rounded-xl border-2 text-sm font-medium text-left transition-all
                        ${sel ? 'bg-amber-50 border-amber-400 text-amber-800' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-amber-300'}`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0
                          ${sel ? 'border-amber-500 bg-amber-500' : 'border-gray-300'}`}>
                          {sel && <span className="w-2 h-2 rounded-full bg-white" />}
                        </span>
                        {item.nombre}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <button
            disabled={!completo}
            onClick={() => onConfirmar(selecciones)}
            className={`w-full py-3.5 rounded-xl font-bold text-white transition-colors
              ${completo ? 'bg-amber-500 hover:bg-amber-600 active:scale-95' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
            {completo ? `Agregar al pedido — ${formatCLP(combo.precio)}` : 'Selecciona todas las opciones'}
          </button>
        </div>
      </div>
    </div>
  );
};
